import React, { useCallback, useEffect, useState } from 'react';
import { Drawer, Tabs, Card, Button, Spin, Empty, message } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { supabase } from '../utils/auth';
import dayjs from 'dayjs';
import './NotificationsDrawer.css';
import {useSelector} from "react-redux";

import {getText, NOTIFICATION_TYPES} from "../utils/helper";
import {logger} from '../utils/logger';

const NotificationsDrawer = ({ open, onClose, onNotificationArchived }) => {
    const lang = useSelector((state) => state.app.lang);
    const [activeTab, setActiveTab] = useState('active');
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(false);

    // Загрузка уведомлений
    const loadNotifications = useCallback(async () => {
        try {
            setLoading(true);
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) return;

            const { data, error } = await supabase
                .from('notifications')
                .select('*')
                .eq('user_id', user.id)
                .order('created_at', { ascending: false });

            if (error) throw error;
            setNotifications(data || []);
        } catch (error) {
            logger.error(`${getText('Ошибка загрузки уведомлений:', lang)}`, error);
            message.error(getText('Не удалось загрузить уведомления', lang));
        } finally {
            setLoading(false);
        }
    }, [lang]);

    useEffect(() => {
        if (open) {
            loadNotifications();
        }
    }, [open, loadNotifications]);

    // Архивация уведомления
    const archiveNotification = useCallback(async (notificationId) => {
        try {
            const { error } = await supabase
                .from('notifications')
                .update({
                    status: 'archived',
                    archived_at: new Date().toISOString()
                })
                .eq('id', notificationId);

            if (error) throw error;

            // Обновляем локальное состояние
            setNotifications(prev =>
                prev.map(n =>
                    n.id === notificationId
                        ? { ...n, status: 'archived', archived_at: new Date().toISOString() }
                        : n
                )
            );

            // Уведомляем родительский компонент об изменении
            if (onNotificationArchived && typeof onNotificationArchived === 'function') {
                onNotificationArchived();
            }
        } catch (error) {
            logger.error('Ошибка архивации уведомления:', error);
            message.error(getText('Не удалось архивировать уведомление', lang));
        }
    }, [onNotificationArchived, lang]);

    // Фильтрация уведомлений
    const activeNotifications = notifications.filter(n => n.status === 'active');
    const archivedNotifications = notifications.filter(n => n.status === 'archived');

    // Получение цвета карточки
    const getCardColor = (color) => {
        const colors = {
            green: '#22c55e',
            yellow: '#eab308',
            red: '#ef4444',
            gray: '#9ca3af'
        };
        return colors[color] || colors.gray;
    };

    // Получение заголовка уведомления на основе типа
    const getNotificationTitle = (notification) => {
        const type = notification.type;
        let textKey;

        if (type === 'rejection') {
            textKey = NOTIFICATION_TYPES.rejection.textKey;
        } else if (type === 'approval') {
            textKey = NOTIFICATION_TYPES.approval.textKey;
        } else if (type === 'drone_registered') {
            textKey = NOTIFICATION_TYPES.drone_registered.textKey;
        } else {
            textKey = NOTIFICATION_TYPES.default.textKey;
        }

        return getText(textKey, lang);
    };

    // Рендер карточки уведомления
    const renderNotificationCard = (notification, isActive = true) => {
        const cardColor = getCardColor(notification.color);
        const moderatorComment = notification.metadata?.moderator_comment;
        const notificationTitle = getNotificationTitle(notification);

        return (
            <Card
                key={notification.id}
                className="notificationCard"
                style={{
                    borderLeft: `4px solid ${cardColor}`,
                    marginBottom: '12px'
                }}
            >
                <div className="notificationCardHeader">
                    <div className="notificationCardDate">
                        {dayjs(notification.created_at).format('DD.MM.YYYY HH:mm')}
                    </div>
                    {isActive && (
                        <Button
                            type="text"
                            icon={<CloseOutlined />}
                            size="small"
                            className="notificationCardClose"
                            onClick={() => archiveNotification(notification.id)}
                        />
                    )}
                </div>
                <div className="notificationCardTitle">
                    {notificationTitle}
                </div>
                {notification.message && (
                    <div className="notificationCardMessage">
                        {notification.message}
                    </div>
                )}
                {moderatorComment && (
                    <div className="notificationCardModeratorComment">
                        <div className="notificationCardModeratorLabel">{getText('Комментарий модератора:', lang)}</div>
                        <div className="notificationCardModeratorText">{moderatorComment}</div>
                    </div>
                )}
            </Card>
        );
    };

    return (
        <Drawer
            title={getText('Уведомления', lang)}
            placement="right"
            onClose={onClose}
            open={open}
            className="notificationsDrawer"
            styles={{
                body: {
                    padding: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%'
                }
            }}
        >
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Tabs
                    activeKey={activeTab}
                    onChange={setActiveTab}
                    items={[
                        {
                            key: 'active',
                            label: `${getText('Активные', lang)} (${activeNotifications.length})`,
                        },
                        {
                            key: 'archived',
                            label: `${getText('Архив', lang)} (${archivedNotifications.length})`,
                        },
                    ]}
                    style={{ flexShrink: 0 }}
                />

                <div className="notificationsContent">
                {loading ? (
                    <div className="notificationsLoading">
                        <Spin size="large" />
                    </div>
                ) : activeTab === 'active' ? (
                    activeNotifications.length === 0 ? (
                        <Empty description={getText('Нет активных уведомлений', lang)} />
                    ) : (
                        <div className="notificationsList">
                            {activeNotifications.map(notification =>
                                renderNotificationCard(notification, true)
                            )}
                        </div>
                    )
                ) : (
                    archivedNotifications.length === 0 ? (
                        <Empty description={getText('Нет архивных уведомлений', lang)} />
                    ) : (
                        <div className="notificationsList">
                            {archivedNotifications.map(notification =>
                                renderNotificationCard(notification, false)
                            )}
                        </div>
                    )
                )}
                </div>
            </div>
        </Drawer>
    );
};

export default NotificationsDrawer;
